import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import {
  IAccountPayableCreate,
  IAccountPayablesWithPayments,
  ICreatePayedPaymentRequestParams,
  IUpdateAccountPayableRequestParams,
} from "../../domain";
import { partnersService } from "../../services/partner.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdatePartnerPlanRequstParams } from "../../domain/IUpdatePartnerPlanRequstParams";
import { IFinancialCard } from "../../domain/payables/IFinancialCard";
import { IUpdatePaidPaymentRequestParams } from "../../domain/receivables/IUpdatePaidPaymentRequestParams";
import { resetPartnerPlansPayable } from "../Main/main.slice";

interface mainState {
  loading: boolean;
  accountPayablesWithPayments: IAccountPayablesWithPayments[];
  allAccPayablesWithPayments: IAccountPayablesWithPayments[];
  payablesTotalInvoicedAmountForPartner: number;
  payablesTotalPaidAmountForPartner: number;
  payablesTotalOutstandingAmountForPartner: number;
  payablesTotalXxxInvoicedAmountForPartner: number;
  payablesTotalXxxPaidAmountForPartner: number;
  payablesTotalXxxOutstandingAmountForPartner: number;
  payablesTotalCashPaidAmountForPartner: number;
  newAccountPayableMopayablesTotalPaidAmountForPartnerdalOpen: boolean;
  newAccountPayablePopover: IPopoverState;
  newAccountPayableModalLoading: boolean;
  deleteAccountPayableModalOpen: boolean;
  indexForDeletionAccountPayable: number;
  newPayedPaymentModalOpen: boolean;
  deletePayedPaymentModalOpen: boolean;
  indexesForPaymentDeletion?: {
    accountIndex: number;
    paymentIndex: number;
  };
  indexForPaymentCreation: number;
  paymentCreationPopover: IPopoverState;
  payablesFinancialCard: IFinancialCard | undefined;
  error: any;
}

const initialState: mainState = {
  loading: false,
  accountPayablesWithPayments: [],
  allAccPayablesWithPayments: [],
  payablesTotalInvoicedAmountForPartner: 0,
  payablesTotalPaidAmountForPartner: 0,
  payablesTotalOutstandingAmountForPartner: 0,
  payablesTotalXxxInvoicedAmountForPartner: 0,
  payablesTotalXxxPaidAmountForPartner: 0,
  payablesTotalXxxOutstandingAmountForPartner: 0,
  payablesTotalCashPaidAmountForPartner: 0,
  newAccountPayableMopayablesTotalPaidAmountForPartnerdalOpen: false,
  newAccountPayablePopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  newAccountPayableModalLoading: false,
  deleteAccountPayableModalOpen: false,
  indexForDeletionAccountPayable: -1,
  newPayedPaymentModalOpen: false,
  deletePayedPaymentModalOpen: false,
  indexesForPaymentDeletion: undefined,
  indexForPaymentCreation: -1,
  paymentCreationPopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  payablesFinancialCard: undefined,
  error: undefined,
};

export const getAllAccountPayables = createAsyncThunk(
  "Partners/getAllAccountPayables",
  async (partnerId: number) => {
    try {
      return await partnersService.getAllAccountPayables(partnerId);
    } catch (err) {
      setError(err);
    }
  }
);

export const createAccountPayable = createAsyncThunk(
  "Partners/createAccountPayable",
  async (data: IAccountPayableCreate, { rejectWithValue }) => {
    try {
      return await partnersService.createAccountPayable(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateAccountPayable = createAsyncThunk(
  "Partners/updateAccountPayable",
  async (data: IUpdateAccountPayableRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updateAccountPayable(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteAccountPayable = createAsyncThunk(
  "Partners/deleteAccountPayable",
  async (accountPayableId: number, { rejectWithValue }) => {
    try {
      return await partnersService.deleteAccountPayable(accountPayableId);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const createPayedPayment = createAsyncThunk(
  "Partners/createPayedPayment",
  async (data: ICreatePayedPaymentRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.createPaidPayment(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updatePaidPayment = createAsyncThunk(
  "Partners/updatePaidPayment",
  async (data: IUpdatePaidPaymentRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updatePaidPayment(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deletePayedPayment = createAsyncThunk(
  "Partners/deletePayedPayment",
  async (paidPaymentId: number, { rejectWithValue }) => {
    try {
      return await partnersService.deletePaidPayment(paidPaymentId);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateAccountPayableAlarm = createAsyncThunk(
  "Partners/updateAccountPayableAlarm",
  async (params: { date: string; note: string; payableId: number }) => {
    try {
      const response = await partnersService.updateAccountPayableAlarm(
        params.date,
        params.note,
        params.payableId
      );
      return { response, payableId: params.payableId };
    } catch (err) {
      setError(err);
    }
  }
);

export const getPayablesFinancialCard = createAsyncThunk(
  "Partners/getPayablesFinancialCard",
  async (partnerId: number) => {
    try {
      return await partnersService.getPayablesFinancialCard(partnerId);
    } catch (err) {
      setError(err);
    }
  }
);


export const accountPayableSlice = createSlice({
  name: "partners-accaountPayable",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setAccountPayables: (state, action: PayloadAction<IAccountPayablesWithPayments[]>) => {
      state.accountPayablesWithPayments = action.payload;

      // Recompute totals from the (filtered) list so TableFooter stays in sync
      // with the rows shown. Mirrors the backend total calculation.
      let invoiced = 0;
      let paid = 0;
      let outstanding = 0;
      let xxxInvoiced = 0;
      let xxxPaid = 0;
      let xxxOutstanding = 0;
      let cashPaid = 0;

      for (const item of action.payload) {
        const amountInvoiced = item.accountPayable.amountInvoiced;
        const amountPaid = item.accountPayable.amountPaid;
        const amountOutstanding = amountInvoiced - amountPaid;
        const amountCash = item.paidPayments.reduce(
          (sum, payment) => sum + (payment.isCash ? payment.amount : 0),
          0
        );

        invoiced += amountInvoiced;
        paid += amountPaid;
        outstanding += amountOutstanding;
        cashPaid += amountCash;

        if (item.accountPayable.isXxx) {
          xxxInvoiced += amountInvoiced;
          xxxPaid += amountPaid;
          xxxOutstanding += amountOutstanding;
        }
      }

      state.payablesTotalInvoicedAmountForPartner = invoiced;
      state.payablesTotalPaidAmountForPartner = paid;
      state.payablesTotalOutstandingAmountForPartner = outstanding;
      state.payablesTotalXxxInvoicedAmountForPartner = xxxInvoiced;
      state.payablesTotalXxxPaidAmountForPartner = xxxPaid;
      state.payablesTotalXxxOutstandingAmountForPartner = xxxOutstanding;
      state.payablesTotalCashPaidAmountForPartner = cashPaid;
    },
    setNewAccountPayablePopover: (
      state,
      action: PayloadAction<IPopoverState>
    ) => {
      state.newAccountPayablePopover = action.payload;
    },
    setIndexForDeletionAccountPayable: (
      state,
      action: PayloadAction<number>
    ) => {
      state.indexForDeletionAccountPayable = action.payload;
      state.deleteAccountPayableModalOpen = action.payload !== -1;
    },
    setIndexForPaymentCreation: (state, action: PayloadAction<number>) => {
      state.indexForPaymentCreation = action.payload;
      state.newPayedPaymentModalOpen = action.payload !== -1;
    },
    setIndexesForPaymentDeletion: (
      state,
      action: PayloadAction<
        { accountIndex: number; paymentIndex: number } | undefined
      >
    ) => {
      state.indexesForPaymentDeletion = action.payload;
      state.deletePayedPaymentModalOpen = action.payload !== undefined;
    },
    setPaymentCreationPopover: (
      state,
      action: PayloadAction<IPopoverState>
    ) => {
      state.paymentCreationPopover = action.payload;
    },
    setNote: (
      state,
      action: PayloadAction<{ payableId: number; note: string }>
    ) => {
      const itemToUpdate = state.accountPayablesWithPayments.find(
        (element) =>
          element.accountPayable.payableId === action.payload.payableId
      );

      if (itemToUpdate) {
        itemToUpdate.accountPayable.note = action.payload.note;
      }
    },
    updateAccountPayableTemporarily: (
      state,
      action: PayloadAction<IUpdateAccountPayableRequestParams>
    ) => {
      for (let payableIndex = 0; payableIndex < state.accountPayablesWithPayments.length; payableIndex++) {
        if (state.accountPayablesWithPayments[payableIndex].accountPayable.payableId === action.payload.accountPayableId) {
          if (action.payload.invoiceId !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.invoiceId = action.payload.invoiceId;
          }
          if (action.payload.projectId !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.projectId = action.payload.projectId;
          }
          if (action.payload.invoiceDate !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.invoiceDate = action.payload.invoiceDate;
          }
          if (action.payload.dueDate !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.dueDate = action.payload.dueDate;
          }
          if (action.payload.amountTotal !== undefined) {
            const oldAmountInvoiced = state.accountPayablesWithPayments[payableIndex].accountPayable.amountInvoiced;
            state.payablesTotalInvoicedAmountForPartner += action.payload.amountTotal - oldAmountInvoiced;
            state.payablesTotalOutstandingAmountForPartner += action.payload.amountTotal - oldAmountInvoiced;
            state.accountPayablesWithPayments[payableIndex].accountPayable.amountInvoiced = action.payload.amountTotal;
          }
          if (action.payload.isXxx !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.isXxx = action.payload.isXxx;
          }
          if (action.payload.note !== undefined) {
            state.accountPayablesWithPayments[payableIndex].accountPayable.note = action.payload.note;
          }
        }
      }
    },
    updatePaidPaymentTemporarily: (
      state,
      action: PayloadAction<IUpdatePaidPaymentRequestParams>
    ) => {
      const payable = state.accountPayablesWithPayments.find(
        item => item.accountPayable.payableId === action.payload.accountPayableId
      );

      if (!payable) return;

      const payment = payable.paidPayments.find(
        p => p.paidPaymentId === action.payload.paidPaymentId
      );

      if (!payment) return;

      const oldAmount = payment.amount;
      const newAmount = action.payload.amount ?? oldAmount;
      const wasCash = payment.isCash;
      const isCash = action.payload.isCash ?? wasCash;
      const amountDifference = newAmount - oldAmount;

      state.payablesTotalCashPaidAmountForPartner +=
        (isCash ? newAmount : 0) - (wasCash ? oldAmount : 0);

      if (action.payload.amount !== undefined) {
        state.payablesTotalPaidAmountForPartner += amountDifference;
        payable.accountPayable.amountPaid += amountDifference;
        payment.amount = newAmount;
      }

      if (action.payload.isCash !== undefined) payment.isCash = isCash;
      if (action.payload.date !== undefined) payment.date = action.payload.date;
    },
    updatePayablePlanTemporarily: (
      state,
      action: PayloadAction<IUpdatePartnerPlanRequstParams>
    ) => {
      for (let payableIndex = 0; payableIndex < state.accountPayablesWithPayments.length; payableIndex++) {
        if (state.accountPayablesWithPayments[payableIndex].accountPayable.payableId === action.payload.accountPayableId) {
          state.accountPayablesWithPayments[payableIndex].planned = action.payload.planned;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAccountPayables.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllAccountPayables.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountPayablesWithPayments = action.payload.accountPayablesWithPayments;
        state.allAccPayablesWithPayments =
          action.payload.accountPayablesWithPayments;
        state.payablesTotalInvoicedAmountForPartner = action.payload.payablesTotalInvoicedAmountForPartner;
        state.payablesTotalOutstandingAmountForPartner = action.payload.payablesTotalOutstandingAmountForPartner;
        state.payablesTotalPaidAmountForPartner = action.payload.payablesTotalPaidAmountForPartner;
        state.payablesTotalXxxInvoicedAmountForPartner = action.payload.payablesTotalXxxInvoicedAmountForPartner;
        state.payablesTotalXxxOutstandingAmountForPartner = action.payload.payablesTotalXxxOutstandingAmountForPartner;
        state.payablesTotalXxxPaidAmountForPartner = action.payload.payablesTotalXxxPaidAmountForPartner;
        state.payablesTotalCashPaidAmountForPartner = action.payload.payablesTotalCashPaidAmountForPartner;
      }
      state.loading = false;
    });
    builder.addCase(getAllAccountPayables.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(createAccountPayable.pending, (state) => {
      state.newAccountPayableModalLoading = true;
    });
    builder.addCase(createAccountPayable.fulfilled, (state) => {
      state.newAccountPayableModalLoading = false;
    });
    builder.addCase(createAccountPayable.rejected, (state, action) => {
      state.newAccountPayableModalLoading = false;
      state.error = action.error;
    });
    builder.addCase(updateAccountPayable.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountPayablesWithPayments =
          state.accountPayablesWithPayments.map((acc) => {
            if (acc.accountPayable.payableId === action.payload.payableId) {
              state.payablesTotalInvoicedAmountForPartner +=
                action.payload.amountInvoiced - acc.accountPayable.amountInvoiced;
              state.payablesTotalOutstandingAmountForPartner +=
                action.payload.amountInvoiced - acc.accountPayable.amountInvoiced;
              return {
                ...acc,
                accountPayable: action.payload,
              };
            } else {
              return acc;
            }
          }
          );
        state.allAccPayablesWithPayments =
          state.allAccPayablesWithPayments.map((acc) => {
            if (acc.accountPayable.payableId === action.payload.payableId) {
              state.payablesTotalInvoicedAmountForPartner +=
                action.payload.amountInvoiced - acc.accountPayable.amountInvoiced;
              state.payablesTotalOutstandingAmountForPartner +=
                action.payload.amountInvoiced - acc.accountPayable.amountInvoiced;
              return {
                ...acc,
                accountPayable: action.payload,
              };
            } else {
              return acc;
            }
          }
          );
      }
    });
    builder.addCase(updateAccountPayable.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(updateAccountPayableAlarm.fulfilled, (state, action) => {
      const updatedAlarm = action.payload?.response;

      const elementToUpdate = state.accountPayablesWithPayments.find(
        (element) =>
          element.accountPayable.payableId === action.payload?.payableId
      );

      if (elementToUpdate) {
        if (updatedAlarm?.date && updatedAlarm.note) {
          elementToUpdate.alarm = updatedAlarm;
        } else {
          elementToUpdate.alarm = undefined;
        }
      }
    });
    builder.addCase(getPayablesFinancialCard.fulfilled, (state, action) => {
      if (action.payload) {
        state.payablesFinancialCard = action.payload;
      }
    });
    builder.addCase(resetPartnerPlansPayable.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountPayablesWithPayments.forEach(payable => {
          payable.planned = false;
        });
      }
    });
  },
});

export const {
  setError,
  setAccountPayables,
  setNewAccountPayablePopover,
  setIndexForDeletionAccountPayable,
  setIndexForPaymentCreation,
  setIndexesForPaymentDeletion,
  setNote,
  setPaymentCreationPopover,
  updateAccountPayableTemporarily,
  updatePaidPaymentTemporarily,
  updatePayablePlanTemporarily,
} = accountPayableSlice.actions;

export const selectState = (state: RootState) => state.partners.accountPayable;

export const selectAccountPayableForDeletion = (state: RootState) =>
  state.partners.accountPayable.accountPayablesWithPayments[
  state.partners.accountPayable.indexForDeletionAccountPayable
  ];

export const selectAccountPayableForPaymentCreation = (state: RootState) =>
  state.partners.accountPayable.accountPayablesWithPayments[
    state.partners.accountPayable.indexForPaymentCreation
  ]?.accountPayable;

export const selectPayablePaymentForDeletion = (state: RootState) => {
  if (state.partners.accountPayable.indexesForPaymentDeletion) {
    const account =
      state.partners.accountPayable.accountPayablesWithPayments[
      state.partners.accountPayable.indexesForPaymentDeletion.accountIndex
      ];
    const reference = account.accountPayable.invoiceId;
    const payment =
      account.paidPayments[
      state.partners.accountPayable.indexesForPaymentDeletion.paymentIndex
      ];
    return {
      reference,
      payment,
    };
  }
  return null;
};

export default accountPayableSlice.reducer;
